import { useEffect, useMemo, useRef } from 'react'

import {
  animateChangeHeatMapByCategories,
  animateHoverPolygon,
  filterPolygonByUrlCode,
  heatMapCaseExpressionColors,
  heatMapColors,
  tileZoomConfiguration
} from '../../utils/heatMapUtils'
import {
  useMapStore,
  usePointStore,
  usePolygonStore
} from '@monopolio-ui/state-library'
import {
  EHeatMapCategory,
  IPolygonProperties
} from '@monopolio-ui/types-library'
import PolygonPopup from './polygonPopup'
import { usePrevious } from '@wiggot/hooks/src/usePrevious'
import { presenter } from '@wiggot/core-apis/src/presenter'

const PropertyManager = (): JSX.Element => {
  const {
    map,
    isLoadedMap,
    drawingTool,
    hoveredLayer,
    clickedLayer

    // setPropertyId,
    // setOffMarketCoords,
  } = useMapStore()

  const { polygonTiles, isEnabledHeatMap, heatMapCategory, polygonNavigation } =
    usePolygonStore()

  const {
    pointsSvgContainer,
    hoveredOffMarketPoint,
    setHoveredOffMarketPoint
  } = usePointStore()

  const heatMapCategoryPrevious = usePrevious<EHeatMapCategory | null>(
    heatMapCategory
  )

  const drawingToolRef = useRef<typeof drawingTool>(drawingTool)
  drawingToolRef.current = drawingTool

  const pointsSvgContainerRef = useRef<any>(pointsSvgContainer)
  pointsSvgContainerRef.current = pointsSvgContainer

  const hoveredOffMarketPointRef = useRef<typeof hoveredOffMarketPoint>(
    hoveredOffMarketPoint
  )
  hoveredOffMarketPointRef.current = hoveredOffMarketPoint

  const hoveredOffMarketPointPrevious = usePrevious<
    typeof hoveredOffMarketPoint
  >(hoveredOffMarketPoint)

  const hoveredLayerRef = useRef<typeof hoveredLayer>(hoveredLayer)
  hoveredLayerRef.current = hoveredLayer

  const heatMapCategoryRef = useRef<EHeatMapCategory | null>(heatMapCategory)
  heatMapCategoryRef.current = heatMapCategory

  const propertiesTiles = useMemo(() => {
    const filter = polygonTiles.filter((tile) => tile?.tiles?.properties?.id)
    return filter.map((tile) => tile?.tiles?.properties)
  }, [polygonTiles])

  /**
   * This function is used to load the properties polygons in the map.
   * @return {void}
   */
  const loadPropertyLevel = (): void => {
    for (const { id } of propertiesTiles) {
      const layerName = `${id}-property-polygon`
      const source = `${id}-property-source`
      const sourceLayer = id

      const find = map?.getSource(source)

      if (find) {
        continue
      }

      const { minZoom, maxZoom } = tileZoomConfiguration.property

      map?.addSource(source, {
        type: 'vector',
        url: `mapbox://davpach.${id}`,
        minzoom: minZoom,
        maxzoom: maxZoom
      })

      const clustersLayer = getPrincipalCluster()
      type TKeyHeatMapColors = keyof typeof heatMapColors
      map?.addLayer(
        {
          id: layerName,
          type: 'fill',
          source,
          'source-layer': sourceLayer,
          paint: {
            'fill-color':
              isEnabledHeatMap && heatMapCategory
                ? [
                    'case',
                    ['boolean', ['feature-state', 'hover'], false],
                    ['feature-state', 'hover_color'],
                    heatMapCaseExpressionColors[
                      heatMapCategory as TKeyHeatMapColors
                    ]
                  ]
                : '#000',
            'fill-opacity':
              isEnabledHeatMap && heatMapCategory
                ? 0.4
                : [
                    'case',
                    ['boolean', ['feature-state', 'hover'], false],
                    0.2,
                    0
                  ]
          }
        },
        clustersLayer
      )

      map?.addLayer(
        {
          id: `${id}-property-border`,
          type: 'line',
          source,
          'source-layer': sourceLayer,

          paint: {
            'line-color':
              isEnabledHeatMap && heatMapCategory
                ? [
                    'case',
                    ['boolean', ['feature-state', 'hover'], false],
                    heatMapColors[heatMapCategory as TKeyHeatMapColors].hover,
                    '#0368FE'
                  ]
                : '#81818d',
            'line-width':
              isEnabledHeatMap && heatMapCategory
                ? [
                    'case',
                    ['boolean', ['feature-state', 'hover'], false],
                    ['feature-state', 'hover_width'],
                    1.678
                  ]
                : 1.678
          }
        },
        clustersLayer
      )

      createHoverEvent({
        layerName,
        source,
        sourceLayer
      })
    }
  }

  const createHoverEvent = (params: {
    layerName: string
    source: string
    sourceLayer: string
  }) => {
    const { layerName, source, sourceLayer } = params

    let featureId: number | undefined

    map?.on('mousemove', layerName, (event) => {
      const feature = map?.queryRenderedFeatures(event.point, {
        layers: [layerName]
      })[0]

      if (!feature) {
        return
      }

      const { id } = feature

      featureId = id as number

      if (
        !featureId ||
        (drawingToolRef.current.isActive &&
          drawingToolRef.current.status !== 'processed') ||
        hoveredLayerRef?.current?.layerId === 'listingPoint' ||
        hoveredLayerRef?.current?.layerId === 'listingStacked' ||
        hoveredOffMarketPointRef?.current?.featureId === id
      ) {
        return
      }

      setHoveredOffMarketPoint({
        featureId,
        sourceId: source,
        sourceLayer,
        layerId: layerName,
        properties: feature?.properties
      })

      setTimeout(() => {
        pointsSvgContainerRef?.current?.style('cursor', 'pointer')
        map.getCanvas().style.cursor = 'pointer'
      }, 50)
    })

    map?.on('mouseleave', layerName, () => {
      if (
        !featureId ||
        (drawingToolRef.current.isActive &&
          drawingToolRef.current.status !== 'processed')
      ) {
        return
      }

      featureId = undefined
      setHoveredOffMarketPoint(undefined)
      pointsSvgContainerRef?.current?.style('cursor', 'grab')
      map.getCanvas().style.cursor = 'grab'
    })
  }

  const getPrincipalCluster = (): string => {
    const layers = map?.getStyle()?.layers || []

    const principalCluster = layers.find((layer: any) => {
      return layer.id.includes('cluster')
    })?.id

    if (!principalCluster) {
      return ''
    }

    return principalCluster
  }

  /**
   * This function is used to click event
   * @returns {void}
   */
  const selectPolygon = (params: { properties: any }): void => {
    const { properties } = params
    const nomenclature = presenter<{
      id: string
      centroid: string
    }>({
      value: properties,
      style: 'camel'
    })
    const { lat, lng } = JSON.parse(nomenclature.centroid)
    // setOffMarketCoords({ lat, lng })
    // setPropertyId(nomenclature.id)
    toCenter({ lat, lng })
  }

  /**
   * This function is used to center the map to the polygon
   * @param lat - The latitude
   * @param lng - The longitude
   * @returns {void}
   */
  const toCenter = (params: { lat: number; lng: number }): void => {
    const { lat, lng } = params

    map?.flyTo({
      center: [lng, lat],
      essential: true,
      speed: 0.7,
      curve: 1.3,
      duration: 300
    })
  }
  const heatMapColor = (heatMapCategoryRef?.current ??
    heatMapCategory) as keyof typeof heatMapColors

  const illuminatePolygon = (params: {
    featureId: number
    sourceId: string
    sourceLayer: string
    properties: any
  }) => {
    const { featureId, sourceId, sourceLayer, properties } = params

    if (!map) {
      return
    }

    if (!isEnabledHeatMap) {
      map?.setFeatureState(
        {
          id: featureId,
          source: sourceId,
          sourceLayer
        },
        {
          hover: true
        }
      )
      return
    }

    if (!properties) {
      // off market point
      map?.setFeatureState(
        {
          source: sourceId,
          sourceLayer,
          id: featureId
        },
        {
          hover: true,
          hover_color: heatMapColors[heatMapColor].hover,
          hover_width: 4
        }
      )
      return
    }

    let indexKey = 'price_index_rank'
    if (heatMapCategoryRef?.current === EHeatMapCategory.RENT) {
      indexKey = 'rent_index_rank'
    } else if (heatMapCategoryRef?.current === EHeatMapCategory.CAPRATE) {
      indexKey = 'caprate_index_rank'
    }

    const codeLevel = properties?.[indexKey]

    const bgColor =
      heatMapColors[heatMapColor][
        codeLevel as keyof (typeof heatMapColors)[EHeatMapCategory]
      ] ?? '#ffffff'

    animateHoverPolygon({
      source: sourceId,
      sourceLayer,
      featureId,
      previousBorder: 1.678,
      nextBorder: 4,
      bgColor,
      bgHoverColor: heatMapColors[heatMapColor].hover,
      duration: 300,
      map
    })

    map?.setFeatureState(
      {
        source: sourceId,
        sourceLayer,
        id: featureId
      },
      {
        hover: true,
        hover_color:
          heatMapColors[heatMapColor][
            codeLevel as keyof (typeof heatMapColors)[EHeatMapCategory]
          ],
        hover_width: 1.678
      }
    )
  }

  const resetPolygon = (params: {
    featureId: number
    sourceId: string
    sourceLayer: string
  }) => {
    const { featureId, sourceId, sourceLayer } = params
    map?.setFeatureState(
      {
        id: featureId,
        source: sourceId,
        sourceLayer
      },
      {
        hover: false
      }
    )
  }

  const resetAllPolygons = () => {
    if (!map) {
      return
    }

    for (const { id } of propertiesTiles) {
      const find = map?.getSource(`${id}-property-source`)

      if (!find) {
        continue
      }

      map.setPaintProperty(`${id}-property-polygon`, 'fill-color', '#000')
      map.setPaintProperty(`${id}-property-polygon`, 'fill-opacity', [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        0.2,
        0
      ])
      map.setPaintProperty(`${id}-property-border`, 'line-color', '#81818d')
      map.setPaintProperty(`${id}-property-border`, 'line-width', 1.678)
    }
  }

  const changePropertyLevelHeatMapColor = (params: {
    category: EHeatMapCategory
    previousCategory: EHeatMapCategory | null
  }): void => {
    if (!map) {
      return
    }

    const { category, previousCategory } = params

    for (const { id } of propertiesTiles) {
      const find = map?.getSource(`${id}-property-source`)

      if (!find) {
        continue
      }

      animateChangeHeatMapByCategories({
        layerName: `${id}-property-polygon`,
        previousCategory: previousCategory || category,
        nextCategory: category,
        duration: 300,
        map
      })

      map.setPaintProperty(`${id}-property-polygon`, 'fill-opacity', 0.4)
      map.setPaintProperty(`${id}-property-border`, 'line-color', [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        heatMapColors[category].hover,
        '#0368FE'
      ])
      map.setPaintProperty(`${id}-property-border`, 'line-width', [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        ['feature-state', 'hover_width'],
        1.678
      ])
    }
  }

  interface TPolygon {
    state: IPolygonProperties
    municipality: IPolygonProperties
    neighborhood: IPolygonProperties
  }

  const filterBlockPolygonsByParent = (): void => {
    if (!map || !isLoadedMap || !polygonTiles?.length || !isEnabledHeatMap) {
      return
    }

    const { state, municipality, neighborhood } = polygonNavigation as TPolygon

    if (!state && !municipality && !neighborhood) {
      resetFilterPropertyPolygons()
      return
    }

    const properties = neighborhood || municipality || state

    for (const { id } of propertiesTiles) {
      if (!id) {
        continue
      }

      filterPolygonByUrlCode({
        map,
        layers: [`${id}-property-polygon`, `${id}-property-border`],
        urlCode: properties?.fullUrlCode ?? ''
      })
    }
  }

  const resetFilterPropertyPolygons = (): void => {
    if (!map || !polygonTiles?.length || !isLoadedMap || !isEnabledHeatMap) {
      return
    }

    for (const { id } of propertiesTiles) {
      const mapLayer = map.getLayer(`${id}-block-polygon`)
      if (!id || !mapLayer) {
        continue
      }
      map.setFilter(`${id}-block-polygon`, null)
      map.setFilter(`${id}-block-border`, null)
    }
  }

  useEffect(() => {
    if (map && isLoadedMap && polygonTiles.length) {
      loadPropertyLevel()
    }
  }, [map, isLoadedMap, polygonTiles])

  useEffect(() => {
    if (!clickedLayer) {
      return
    }

    const isPoint = clickedLayer.layerId.includes('-polygon')

    if (!isPoint) {
      return
    }

    selectPolygon({
      properties: clickedLayer?.properties
    })
  }, [clickedLayer])

  useEffect(() => {
    if (
      (!hoveredOffMarketPoint ||
        hoveredOffMarketPoint.featureId !==
          hoveredOffMarketPointPrevious?.featureId) &&
      hoveredOffMarketPointPrevious?.sourceId
    ) {
      const { featureId, sourceId, sourceLayer } = hoveredOffMarketPointPrevious

      resetPolygon({
        featureId,
        sourceId,
        sourceLayer
      })
    }

    if (hoveredOffMarketPoint?.sourceId) {
      const { featureId, sourceId, sourceLayer, properties } =
        hoveredOffMarketPoint

      illuminatePolygon({
        featureId,
        sourceId,
        sourceLayer,
        properties
      })
    }
  }, [hoveredOffMarketPoint])

  useEffect(() => {
    if (!map) {
      return
    }

    if (!isEnabledHeatMap) {
      resetAllPolygons()
      return
    }

    if (heatMapCategory) {
      changePropertyLevelHeatMapColor({
        category: heatMapCategory,
        previousCategory: heatMapCategoryPrevious
      })
    }
  }, [heatMapCategory, isEnabledHeatMap, map])

  useEffect(filterBlockPolygonsByParent, [
    (polygonNavigation as TPolygon)?.state,
    (polygonNavigation as TPolygon)?.municipality,
    (polygonNavigation as TPolygon)?.neighborhood,
    map,
    isLoadedMap,
    polygonTiles,
    isEnabledHeatMap
  ])

  return (
    <>
      {propertiesTiles.map((state) => (
        <PolygonPopup
          key={state.id}
          layer={`${state.id}-property-polygon`}
          level="property"
        />
      ))}
    </>
  )
}

export default PropertyManager
